<template>
  <VdPage class="user-inspector-page" title="User Inspector">
    <template #actions>
      <div class="user-inspector-search">
        <InputText v-model="userUUID" class="uuid-input" placeholder="Paste a user UUID…" @keyup.enter="onInspect" />
        <VdButton
          type="primary"
          skin="blue"
          icon-left="fa:magnifying-glass"
          :disabled="!userUUID.trim() || isLoading"
          @click="onInspect"
        >
          Inspect
        </VdButton>
        <VdButton v-if="assignments.length" type="secondary" skin="blue" icon-left="fa:flask" @click="onOpenSimulator">
          Simulate allocations
        </VdButton>
      </div>
    </template>

    <DataTable
      :value="assignments"
      :loading="isLoading"
      class="primevue-migrated"
      data-key="abTestId"
      removable-sort
      sort-field="createdAt"
      :sort-order="-1"
    >
      <template #empty>
        <span v-if="hasSearched && !isLoading">No active AB test assignments for this user.</span>
        <span v-else>Enter a user UUID and click Inspect to see the user's AB test assignments.</span>
      </template>

      <Column header="A/B Test" field="abTestName" sortable>
        <template #body="{ data }">
          <div class="cell-primary">{{ data.abTestName }}</div>
          <div class="cell-secondary">{{ data.abTestId }}</div>
        </template>
      </Column>

      <Column header="Cohort" field="cohortName" sortable>
        <template #body="{ data }">
          <div class="cell-primary">{{ data.cohortName ?? "—" }}</div>
          <div v-if="data.clusterId" class="cell-secondary">{{ data.clusterId }}</div>
        </template>
      </Column>

      <Column header="Segment" field="segments" sortable>
        <template #body="{ data }">
          {{ data.segments || "—" }}
        </template>
      </Column>

      <Column field="kind" sortable>
        <template #header>
          <span class="kind-header">
            Kind
            <VdTooltip
              content="How the user was assigned. System: automatically allocated by a running A/B test. Manual: manually forced (e.g. a test-device assignment), which overrides System allocations."
            >
              <i class="pi pi-info-circle" />
            </VdTooltip>
          </span>
        </template>
        <template #body="{ data }">
          <span :class="['kind-pill', data.kind.toLowerCase()]">{{ data.kind }}</span>
        </template>
      </Column>

      <Column header="Assigned at" field="createdAt" sortable>
        <template #body="{ data }">
          {{ data.createdAt ? formatAssignedAt(data.createdAt) : "—" }}
        </template>
      </Column>
    </DataTable>
  </VdPage>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { RouteLocationRaw, useRouter } from "vue-router";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import InputText from "primevue/inputtext";
import { VdButton, VdPage, VdTooltip } from "@voodoo.io/voodoo-ui";
import links from "@/router/links";
import useUserInspector from "@/modules/UserInspector/hooks/useUserInspector";

const props = defineProps({
  gameId: {
    type: String,
    required: true,
  },
  versionId: {
    type: String,
    required: true,
  },
});

const router = useRouter();

const { assignments, isLoading, hasSearched, inspect } = useUserInspector();

const userUUID = ref("");

function onInspect() {
  inspect(props.gameId, props.versionId, userUUID.value);
}

/**
 * The Configuration Simulator's CohortSelector expects `abTestCohortIds` as a flat,
 * comma-separated list mixing AB test ids and their cohort (cluster) ids. Each row here
 * contributes its abTestId + clusterId, so the simulator opens with these allocations selected.
 */
const simulatorRoute = computed<RouteLocationRaw>(() => {
  const abTestCohortIds = new Set<string>();
  assignments.value.forEach((assignment) => {
    if (assignment.abTestId) {
      abTestCohortIds.add(assignment.abTestId);
    }
    if (assignment.clusterId) {
      abTestCohortIds.add(assignment.clusterId);
    }
  });

  return {
    name: links.games.simulator,
    params: { gameId: props.gameId, versionId: props.versionId },
    query: { abTestCohortIds: Array.from(abTestCohortIds).join(",") },
  };
});

function onOpenSimulator() {
  router.push(simulatorRoute.value);
}

function formatAssignedAt(timestamp: number): string {
  return new Date(timestamp).toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
    timeZoneName: "short",
  });
}
</script>

<style scoped lang="scss">
.user-inspector-search {
  display: flex;
  gap: 8px;
  align-items: center;

  .uuid-input {
    width: 360px;
  }
}

.cell-primary {
  font-weight: 500;
}

.cell-secondary {
  font-size: 12px;
  color: var(--vd-color-neutral-500, #8a8f98);
}

.kind-pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;

  &.manual {
    background: #fde8cf;
    color: #9a5b00;
  }

  &.system {
    background: #d9e8ff;
    color: #1b4fa0;
  }
}

.kind-header {
  display: inline-flex;
  align-items: center;
  gap: 6px;

  .pi-info-circle {
    font-size: 13px;
    color: var(--vd-color-neutral-500, #8a8f98);
    cursor: help;
  }
}
</style>
